/* eslint-disable import/no-cycle */
import { httpService } from '../../http-service/httpService';

import {
  AXIOS_TIMEOUT,
  PRELAUNCH_DETAIL_ERROR,
  PRELAUNCH_DETAIL_LOADING,
  PRELAUNCH_DOWNLOAD_ERROR,
  PRELAUNCH_DOWNLOAD_LOADING,
  PRELAUNCH_FEATURE_DISABLED,
  PRELAUNCH_FORBIDDEN,
  PRELAUNCH_HISTORY_ERROR,
  PRELAUNCH_HISTORY_LOADING,
  PRELAUNCH_REPORT_ERROR,
  PRELAUNCH_REPORT_LOADING,
  PRELAUNCH_RESET,
  PRELAUNCH_SET_ACTIVE_TAB,
  PRELAUNCH_SET_CAMPAIGN_ID,
  PRELAUNCH_SET_DETAIL,
  PRELAUNCH_SET_FILTER_OPTIONS,
  PRELAUNCH_SET_HISTORY,
  PRELAUNCH_SET_REPORT,
  PRELAUNCH_SET_STATUS,
  PRELAUNCH_STATUS_ERROR,
  PRELAUNCH_STATUS_LOADING,
  PRELAUNCH_TRIGGER_CONFLICT,
  PRELAUNCH_TRIGGER_ERROR,
  PRELAUNCH_TRIGGER_LOADING,
  PRELAUNCH_TRIGGER_SUCCESS,
} from './types';
import {
  URL_PRELAUNCH_DETAIL,
  URL_PRELAUNCH_DOWNLOAD,
  URL_PRELAUNCH_FILTER_OPTIONS,
  URL_PRELAUNCH_HISTORY,
  URL_PRELAUNCH_LAUNCH,
  URL_PRELAUNCH_PREVIEW,
  URL_PRELAUNCH_REPORT,
  URL_PRELAUNCH_RETRIGGER,
  URL_PRELAUNCH_STATUS,
  URL_PRELAUNCH_TRIGGER,
} from './urls';

const timeoutConfig = { timeout: AXIOS_TIMEOUT };

// ── Dispatch the correct action for v3 backend error codes ────────────────────
function dispatchTriggerError(dispatch, errorCode, errorMessage) {
  if (errorCode === 'FEATURE_DISABLED') {
    dispatch({ type: PRELAUNCH_FEATURE_DISABLED });
  } else if (errorCode === 'FORBIDDEN') {
    dispatch({ type: PRELAUNCH_FORBIDDEN });
  } else if (errorCode === 'ALREADY_RUNNING') {
    dispatch({
      type: PRELAUNCH_TRIGGER_CONFLICT,
      payload: { message: errorMessage },
    });
  } else {
    dispatch({
      type: PRELAUNCH_TRIGGER_ERROR,
      payload: errorMessage || 'Trigger failed',
    });
  }
}

// ── Transform API response to flat camelCase shape expected by UI ─────────────

/** Convert snake_case to camelCase */
function toCamel(s) {
  return s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

/**
 * The backend returns nested/array-based data:
 *   summary: { totalUsers, ... }
 *   dataQuality: [{ key: "missing_emails", count: 1, severity: "WARNING" }, ...]
 *   riskAnalysis, certifierDq, typeSpecificDq, typeSpecificRisk: same shape
 *   guardrails, blockers: arrays
 *
 * The UI (PreLaunchDashboard) expects flat camelCase properties on reportData:
 *   r.totalUsers, r.missingEmails, r.criticalEntitlements, r.blockersJson, etc.
 */
export function flattenReportResponse(raw) {
  const flat = {
    success: raw.success,
    timestamp: raw.timestamp,
    reportId: raw.reportId,
    campaignId: raw.campaignId,
    campaignName: raw.campaignName,
    runNumber: raw.runNumber,
    status: raw.status,
    startedAt: raw.startedAt,
    completedAt: raw.completedAt,
    executionTimeMs: raw.executionTimeMs,
    campaignType: raw.campaignType,
  };

  // Flatten summary counts — coerce to number to prevent NaN display
  if (raw.summary) {
    const safeSummary = {};

    Object.entries(raw.summary).forEach(([key, val]) => {
      safeSummary[key] =
        val != null && val !== '' && !Number.isNaN(Number(val))
          ? Number(val)
          : val;
    });
    Object.assign(flat, safeSummary);
  }

  // Flatten metric arrays: extract key→count as camelCase properties
  const metricArrays = [
    'dataQuality',
    'riskAnalysis',
    'certifierDq',
    'typeSpecificDq',
    'typeSpecificRisk',
  ];

  metricArrays.forEach((arrName) => {
    if (Array.isArray(raw[arrName])) {
      raw[arrName].forEach((m) => {
        if (m.key) {
          flat[toCamel(m.key)] = m.count != null ? Number(m.count) : null;
        }
      });
    }
  });

  // Preserve raw metric arrays for direct use in PreLaunchDashboard
  // Ensure count is always a valid number to prevent NaN display
  const safeCount = (val) => {
    if (val == null) return null;
    const n = Number(val);

    return Number.isNaN(n) ? val : n;
  };

  flat.dataQuality = (raw.dataQuality || []).map((m) => ({
    ...m,
    count: safeCount(m.count),
  }));
  flat.riskAnalysis = (raw.riskAnalysis || []).map((m) => ({
    ...m,
    count: safeCount(m.count),
  }));
  flat.certifierDq = (raw.certifierDq || []).map((m) => ({
    ...m,
    count: safeCount(m.count),
  }));
  flat.typeSpecificDq = (raw.typeSpecificDq || []).map((m) => ({
    ...m,
    count: safeCount(m.count),
  }));
  flat.typeSpecificRisk = (raw.typeSpecificRisk || []).map((m) => ({
    ...m,
    count: safeCount(m.count),
  }));

  // Preserve blockers as JSON string for handleLaunch()
  flat.blockersJson = raw.blockers ? JSON.stringify(raw.blockers) : '[]';

  return flat;
}

// ── Simple sync actions ───────────────────────────────────────────────────────

export const setPreLaunchCampaignId = (campaignId) => ({
  type: PRELAUNCH_SET_CAMPAIGN_ID,
  payload: campaignId,
});

export const setPreLaunchActiveTab = (tab) => ({
  type: PRELAUNCH_SET_ACTIVE_TAB,
  payload: tab,
});

export const resetPreLaunch = () => ({ type: PRELAUNCH_RESET });

// ── Trigger pre-launch analysis ───────────────────────────────────────────────
/**
 * POST /ECM/api/v5/campaignPreAnalysis
 * Body: { campaignId, ...optional overrides }
 * Maps to PreLaunchAnalysisService.createReportShell + scheduleJob
 */
export const triggerPreLaunchAnalysis =
  (campaignId, extraParams = {}) =>
  async (dispatch) => {
    dispatch({ type: PRELAUNCH_TRIGGER_LOADING, payload: true });

    try {
      const body = { campaignId: String(campaignId), ...extraParams };
      const { data } = await httpService.post(
        URL_PRELAUNCH_TRIGGER,
        body,
        timeoutConfig
      );

      if (!data?.success) {
        dispatchTriggerError(dispatch, data?.error?.code, data?.error?.message);

        return;
      }

      // data: { success, reportId, runNumber, jobId, status, message, timestamp }
      dispatch({ type: PRELAUNCH_TRIGGER_SUCCESS, payload: data });
      // Immediately seed the status slice so the progress bar can render
      dispatch({
        type: PRELAUNCH_SET_STATUS,
        payload: {
          reportId: data.reportId,
          campaignId,
          runNumber: data.runNumber,
          status: 'RUNNING',
          progressPct: 0,
          jobId: data.jobId,
          startedAt: data.timestamp,
          completedAt: null,
          executionTimeMs: null,
          errorMessage: null,
        },
      });
    } catch (err) {
      dispatch({
        type: PRELAUNCH_TRIGGER_ERROR,
        payload: err?.message || 'Trigger failed',
      });
    }
  };

// ── Poll status ───────────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/getPreLaunchStatus?campaignId=X  or  ?reportId=X
 * Maps to PreLaunchCampaignController.getPreLaunchStatus + computeProgressPct
 * Phase mapping: 1=25%, 2=50%, 3=75%, 4=95%, 5=100%
 */
export const getPreLaunchStatus =
  ({ campaignId, reportId } = {}) =>
  async (dispatch) => {
    dispatch({ type: PRELAUNCH_STATUS_LOADING, payload: true });

    try {
      const params = reportId
        ? { reportId, _: Date.now() }
        : { campaignId, _: Date.now() };
      const { data } = await httpService.get(URL_PRELAUNCH_STATUS, { params });

      if (!data?.success) {
        dispatch({
          type: PRELAUNCH_STATUS_ERROR,
          payload: data?.error?.message || 'Status fetch failed',
        });

        return;
      }

      // data: { success, reportId, campaignId, runNumber, status, progressPct,
      //         jobId, startedAt, completedAt, executionTimeMs, errorMessage }
      dispatch({ type: PRELAUNCH_SET_STATUS, payload: data });
    } catch (err) {
      dispatch({
        type: PRELAUNCH_STATUS_ERROR,
        payload: err?.message || 'Status fetch failed',
      });
    }
  };

// ── Retrigger ─────────────────────────────────────────────────────────────────
/**
 * POST /ECM/api/v5/retriggerPreLaunch
 * Body: { campaignId }
 * Reuses the most recent campaignConfigSnapshot — same logic as initial trigger.
 */
export const retriggerPreLaunchAnalysis = (campaignId) => async (dispatch) => {
  dispatch({ type: PRELAUNCH_TRIGGER_LOADING, payload: true });

  try {
    const { data } = await httpService.post(
      URL_PRELAUNCH_RETRIGGER,
      { campaignId: String(campaignId) },
      timeoutConfig
    );

    if (!data?.success) {
      dispatchTriggerError(dispatch, data?.error?.code, data?.error?.message);

      return;
    }

    dispatch({ type: PRELAUNCH_TRIGGER_SUCCESS, payload: data });
    dispatch({
      type: PRELAUNCH_SET_STATUS,
      payload: {
        reportId: data.reportId,
        campaignId,
        runNumber: data.runNumber,
        status: 'RUNNING',
        progressPct: 0,
        jobId: data.jobId,
        startedAt: data.timestamp,
        completedAt: null,
        executionTimeMs: null,
        errorMessage: null,
      },
    });
  } catch (err) {
    dispatch({
      type: PRELAUNCH_TRIGGER_ERROR,
      payload: err?.message || 'Retrigger failed',
    });
  }
};

// ── Full report ───────────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/getPreLaunchReport?reportId=X
 * Returns all PreLaunchReport fields:
 *   scope counts, 10 DQ metrics, 7 risk metrics, certifier DQ,
 *   type-specific metrics (UM/EO/SA), blockers
 * Note: reportId is required — campaignId is no longer accepted by the backend.
 */
export const getPreLaunchReport =
  ({ reportId } = {}) =>
  async (dispatch) => {
    dispatch({ type: PRELAUNCH_REPORT_LOADING, payload: true });

    try {
      const params = { reportId, _: Date.now() };
      const { data } = await httpService.get(URL_PRELAUNCH_REPORT, { params });

      if (!data?.success) {
        dispatch({
          type: PRELAUNCH_REPORT_ERROR,
          payload: data?.error?.message || 'Report fetch failed',
        });

        return;
      }

      dispatch({
        type: PRELAUNCH_SET_REPORT,
        payload: flattenReportResponse(data),
      });
    } catch (err) {
      dispatch({
        type: PRELAUNCH_REPORT_ERROR,
        payload: err?.message || 'Report fetch failed',
      });
    }
  };

// ── Run history ───────────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/getPreLaunchReportHistory?campaignId=X
 * Returns { runs: [{reportId, runNumber, status, startedAt, completedAt, createdBy}], limit, totalReturned }
 */
export const getPreLaunchReportHistory = (campaignId) => async (dispatch) => {
  dispatch({ type: PRELAUNCH_HISTORY_LOADING, payload: true });

  try {
    const { data } = await httpService.get(URL_PRELAUNCH_HISTORY, {
      params: { campaignId, _: Date.now() },
    });

    if (!data?.success) {
      dispatch({
        type: PRELAUNCH_HISTORY_ERROR,
        payload: data?.error?.message || 'History fetch failed',
      });

      return;
    }

    // data.runs – array of history summaries
    dispatch({ type: PRELAUNCH_SET_HISTORY, payload: data.runs || [] });
  } catch (err) {
    dispatch({
      type: PRELAUNCH_HISTORY_ERROR,
      payload: err?.message || 'History fetch failed',
    });
  }
};

// ── Drill-down detail ─────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/getPreLaunchDetail?reportId=X&metric=<key>&size=50[&searchAfter=<cursor>]
 * Cursor-based pagination: first page omits searchAfter; subsequent pages pass nextSearchAfter
 * from the previous response. Returns { records, total, size, metric, nextSearchAfter, columnLabels }.
 */
export const getPreLaunchDetail =
  ({ reportId, metric, page = 1, size = 50, filter } = {}) =>
  async (dispatch) => {
    dispatch({ type: PRELAUNCH_DETAIL_LOADING, payload: true });

    try {
      const params = { reportId, metric, page, size, _: Date.now() };

      if (filter) params.filter = filter;
      const { data } = await httpService.get(URL_PRELAUNCH_DETAIL, { params });

      if (!data?.success) {
        dispatch({
          type: PRELAUNCH_DETAIL_ERROR,
          payload: data?.error?.message || 'Detail fetch failed',
        });

        return;
      }

      dispatch({ type: PRELAUNCH_SET_DETAIL, payload: data });
    } catch (err) {
      dispatch({
        type: PRELAUNCH_DETAIL_ERROR,
        payload: err?.message || 'Detail fetch failed',
      });
    }
  };

// ── Filter options ────────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/getPreLaunchFilterOptions?reportId=X&metric=<key>&fields=status,department
 * Returns { filters: { field: [{value, count}] } } for drill-down filter dropdowns.
 */
export const getPreLaunchFilterOptions = () => async (dispatch) => {
  try {
    const { data } = await httpService.get(URL_PRELAUNCH_FILTER_OPTIONS, {
      params: { _: Date.now() },
    });

    if (data?.success) {
      dispatch({ type: PRELAUNCH_SET_FILTER_OPTIONS, payload: data });
    }
  } catch (err) {
    // non-critical; silently ignore
  }
};

// ── Download report ───────────────────────────────────────────────────────────
/**
 * GET /ECM/api/v5/downloadPreLaunchReport?reportId=X&format=xlsx
 * Triggers a browser file download.
 */
export const downloadPreLaunchReport =
  (reportId, format = 'xlsx') =>
  async (dispatch) => {
    dispatch({ type: PRELAUNCH_DOWNLOAD_LOADING, payload: true });

    try {
      const response = await httpService.get(URL_PRELAUNCH_DOWNLOAD, {
        params: { reportId, format },
        responseType: 'blob',
        timeout: AXIOS_TIMEOUT,
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      const rawFileName = fileNameMatch
        ? fileNameMatch[1].replace(/['"]/g, '')
        : `prelaunch_report_${reportId}.${format}`;
      // Sanitize filename — strip any characters outside safe file name set to prevent header injection
      const fileName = rawFileName.replace(/[^a-zA-Z0-9._\-() ]/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      dispatch({ type: PRELAUNCH_DOWNLOAD_LOADING, payload: false });
    } catch (err) {
      dispatch({
        type: PRELAUNCH_DOWNLOAD_ERROR,
        payload: err?.message || 'Download failed',
      });
    }
  };

// ── Launch campaign from pre-launch ───────────────────────────────────────────
/**
 * POST /ECM/api/v5/launchFromPreLaunch
 * Body: { campaignId }
 */
export const launchFromPreLaunch = (campaignId) => async () => {
  const response = await httpService.post(
    URL_PRELAUNCH_LAUNCH,
    { campaignId },
    timeoutConfig
  );

  return response.data;
};

// ── Preview campaign from pre-launch ──────────────────────────────────────────
/**
 * POST /ECM/api/v5/previewFromPreLaunch
 * Body: { campaignId }
 */
export const previewFromPreLaunch = (campaignId) => async () => {
  const response = await httpService.post(
    URL_PRELAUNCH_PREVIEW,
    { campaignId },
    timeoutConfig
  );

  return response.data;
};
